import AppAlert from '@nxsys/components/AppAlert';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import { useGetNestSetting } from '@nxsys/hooks/api/company';
import { useGetNestExportPeriod } from '@nxsys/hooks/api/report';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Form, FormInstance } from 'antd';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';

// Static NEST H-line frequency options (NX-5230). NEST accepts both the
// tax-aligned and calendar-aligned variants; the user picks which to write.
const NEST_FREQUENCY_OPTIONS = [
  'Tax Weekly',
  'Tax Monthly',
  'Weekly',
  'Monthly',
  'Fortnightly',
  'Four Weekly',
].map((v) => ({ label: v, value: v }));

// Sensible default frequency per pay frequency; user can override.
const DEFAULT_NEST_FREQUENCY: Record<string, string> = {
  WEEKLY: 'Tax Weekly',
  '2_WEEKLY': 'Fortnightly',
  '4_WEEKLY': 'Four Weekly',
  MONTHLY: 'Tax Monthly',
};

type NestExportSettingsProps = {
  form: FormInstance;
  companyId?: string;
  payFrequency?: string;
  taxPeriod?: number;
  taxYear?: number;
};

const NestExportSettings: React.FC<NestExportSettingsProps> = ({
  form,
  companyId,
  payFrequency,
  taxPeriod,
  taxYear,
}) => {
  const { messages } = useTypedIntl();
  const { value: nestSetting, run: getNestSetting } = useGetNestSetting();
  const { run: getExportPeriod } = useGetNestExportPeriod();

  // True when the period has a matched NEST schedule (State A) — hides the
  // skip-schedule warning and prefills every header cell from the schedule.
  const [hasSchedule, setHasSchedule] = useState(false);

  // Fields the user has manually edited — never overwrite these with a
  // computed default/prefill.
  const dirty = useRef({
    paymentSource: false,
    nestFrequency: false,
    startDate: false,
    endDate: false,
    dueDate: false,
  });

  const startDate = Form.useWatch('nestStartDate', form);
  const endDate = Form.useWatch('nestEndDate', form);
  const dueDate = Form.useWatch('nestDueDate', form);
  const paymentSource = Form.useWatch('nestPaymentSource', form);
  const nestFrequency = Form.useWatch('nestFrequency', form);

  // Load NEST settings for the selected company.
  useEffect(() => {
    if (companyId) getNestSetting(companyId);
  }, [companyId]);

  // Clear manual-edit flags whenever the period selection changes so the
  // prefill below is authoritative for the new period — otherwise a manually
  // edited cell (e.g. Payment Due Date) would linger across a period switch
  // and satisfy the required-field gate with the previous period's value.
  useEffect(() => {
    dirty.current = {
      paymentSource: false,
      nestFrequency: false,
      startDate: false,
      endDate: false,
      dueDate: false,
    };
  }, [companyId, payFrequency, taxPeriod, taxYear]);

  const paymentSourceOptions = useMemo(() => {
    const sources = nestSetting?.nestPaymentSources;
    if (!Array.isArray(sources)) return [];
    return sources.map((s: any) => {
      const name = s?.paymentSourceName || s?.name || s;
      return { label: name, value: name };
    });
  }, [nestSetting?.nestPaymentSources]);

  // Prefill the header fields, unless the user has edited them.
  //  - State A (period has a linked NEST schedule): fill all 5 cells from the
  //    schedule and hide the skip-schedule warning.
  //  - State B (no schedule): start/end from the tax period, payment source /
  //    frequency from the company's NEST settings defaults.
  useEffect(() => {
    if (!companyId || !payFrequency || !taxPeriod) return;
    const set = (field: string, value?: string) => {
      if (value !== undefined) form.setFieldValue(field, value);
    };
    getExportPeriod({ companyId, payFrequency, taxPeriod, taxYear }).then(
      (res) => {
        if (!res) return;
        const sch = res.schedule;
        setHasSchedule(!!sch);
        if (sch) {
          if (!dirty.current.startDate) set('nestStartDate', sch.startDate);
          if (!dirty.current.endDate) set('nestEndDate', sch.endDate);
          if (!dirty.current.dueDate) set('nestDueDate', sch.dueDate);
          if (!dirty.current.paymentSource)
            set('nestPaymentSource', sch.paymentSource);
          if (!dirty.current.nestFrequency)
            set('nestFrequency', sch.nestFrequency);
          return;
        }
        // State B — manual/skip prefill. Reset EVERY cell deterministically
        // (not just start/end) so values carried over from a State A period
        // don't linger when switching to a no-schedule period: dueDate has no
        // local source → cleared for the user to enter; paymentSource falls
        // back to the settings default or empty.
        if (!dirty.current.startDate) set('nestStartDate', res.startOn);
        if (!dirty.current.endDate) set('nestEndDate', res.endOn);
        if (!dirty.current.dueDate) set('nestDueDate', '');
        const defaultSource = (nestSetting?.frequencySettings as any)?.[
          payFrequency
        ]?.paymentSource;
        if (!dirty.current.paymentSource)
          set('nestPaymentSource', defaultSource || '');
        if (!dirty.current.nestFrequency)
          set('nestFrequency', DEFAULT_NEST_FREQUENCY[payFrequency] || '');
      },
    );
  }, [companyId, payFrequency, taxPeriod, taxYear, nestSetting]);

  const requiredRule = [
    { required: true, message: messages['form.pleaseSelect'] },
  ];

  const setDate = (field: 'nestStartDate' | 'nestEndDate' | 'nestDueDate') => (
    date: unknown,
  ) => {
    if (field === 'nestStartDate') dirty.current.startDate = true;
    if (field === 'nestEndDate') dirty.current.endDate = true;
    if (field === 'nestDueDate') dirty.current.dueDate = true;
    form.setFieldValue(
      field,
      date ? moment(date as moment.Moment).format('YYYY-MM-DD') : undefined,
    );
  };

  return (
    <>
      <div className="mt-[8px] mb-[4px] font-semibold">
        {messages['pension.nest.contributionDetails'] ||
          'NEST contribution details'}
      </div>

      {!hasSchedule && (
        <AppAlert
          variant="warning"
          type="warning"
          message={
            messages['pension.nest.skipScheduleWarning'] ||
            'No matched NEST schedule for this period. The file may be rejected by NEST — check the values before uploading.'
          }
          style={{ marginBottom: '12px' }}
        />
      )}

      <Form.Item
        label={messages['pension.nest.contributionStart'] || 'Contribution Start'}
        name="nestStartDate"
        rules={requiredRule}
      >
        <AppDatePicker
          className="w-full"
          value={startDate}
          onChangeValue={setDate('nestStartDate')}
        />
      </Form.Item>

      <Form.Item
        label={messages['pension.nest.contributionEnd'] || 'Contribution End'}
        name="nestEndDate"
        rules={requiredRule}
      >
        <AppDatePicker
          className="w-full"
          value={endDate}
          onChangeValue={setDate('nestEndDate')}
        />
      </Form.Item>

      <Form.Item
        label={messages['pension.nest.dueDate'] || 'Payment Due Date'}
        name="nestDueDate"
        rules={requiredRule}
      >
        <AppDatePicker
          className="w-full"
          value={dueDate}
          onChangeValue={setDate('nestDueDate')}
        />
      </Form.Item>

      <Form.Item
        label={messages['pension.nest.paymentSource'] || 'Payment Source'}
        name="nestPaymentSource"
        rules={requiredRule}
      >
        {paymentSourceOptions.length ? (
          <AppSelect
            className="w-full !min-w-0"
            placeholder={messages['form.select']}
            options={paymentSourceOptions}
            value={
              paymentSource
                ? { label: paymentSource, value: paymentSource }
                : undefined
            }
            onChange={(opt: any) => {
              dirty.current.paymentSource = true;
              form.setFieldValue('nestPaymentSource', opt?.value);
            }}
          />
        ) : (
          <AppInput
            placeholder={
              messages['pension.nest.noPaymentSources'] ||
              'No sources found — enter payment source'
            }
            value={paymentSource}
            onChange={(e: any) => {
              dirty.current.paymentSource = true;
              form.setFieldValue('nestPaymentSource', e?.target?.value);
            }}
          />
        )}
      </Form.Item>

      <Form.Item
        label={messages['pension.nest.frequency'] || 'NEST Frequency'}
        name="nestFrequency"
        rules={requiredRule}
      >
        <AppSelect
          className="w-full !min-w-0"
          placeholder={messages['form.select']}
          options={NEST_FREQUENCY_OPTIONS}
          value={
            nestFrequency
              ? { label: nestFrequency, value: nestFrequency }
              : undefined
          }
          onChange={(opt: any) => {
            dirty.current.nestFrequency = true;
            form.setFieldValue('nestFrequency', opt?.value);
          }}
        />
      </Form.Item>
    </>
  );
};

export default NestExportSettings;
